import React, { forwardRef, useImperativeHandle, useCallback, useState, useRef, useEffect } from 'react';
import KeyboardInput from '../KeyboardInput';
import { useLabels } from '../../hooks';
import { formatString } from '../../utils/helpers';
import styles from './FormInputField.module.scss';

const FormInputField = forwardRef(({
    label, 
    className = '', 
    onChange = () => {},
    onBlur = () => {},
    value,
    error,
    required = false,
    minLength = 0,
    passwordValidation = false,
    emailValidation = false,
    telValidation = false,
    validationPattern = () => {},
    type = 'text',
    hePlaceholderDirection = '',
    heContentDirection = '',
    ...props
}, ref) => {
    const [ valueState, setValueState ] = useState('');
    const [ errorState, setErrorState ] = useState('');
    const [ showPassword, setShowPassword ] = useState(false);
    const inputRef = useRef();
    const isValidationRunRef = useRef(false);
    const { getLabel } = useLabels();

    useEffect(() => {
        if (value || value === '') {
            setValueState(value);
        }
    }, [value]);

    useEffect(() => {
        if (error) {
            setErrorState(error);
        }
    }, [error]);

    const validation = useCallback((value) => {
        isValidationRunRef.current = true;
        
        if (error) {
            setErrorState(error);
            return false;
        }

        const data = value || value === '' ? value : valueState;

        if (required && !data) {
            setErrorState(getLabel('form_input_field_required_error'));
            return false;
        }

        if (minLength && minLength > 0 && minLength > data.length) {
            setErrorState(formatString(getLabel('form_input_field_minlength_error'), minLength));
            return false;
        }

        if (emailValidation && data) {
            if(!(/^[a-zA-Z0-9.+_-]+@[a-zA-Z0-9._-]+\.[a-zA-Z]{2,4}$/).test(data)){
                setErrorState(getLabel('form_input_field_email_error'));
                return false;
            }
        }

        if (telValidation && data) {
            if(!(/^(\+[1-9])?[0-9]{0,24}$/).test(data)) {
                setErrorState(getLabel('form_input_field_tel_error'));
                return false;
            }
        }

        if (passwordValidation) {
            if (data.length < 8) {
                setErrorState(formatString(getLabel('form_input_field_password_length_error'), 8));
                return false;
            }
            if ((/[א-ת]/).test(data)) {
                setErrorState(getLabel('form_input_field_password_he_error'));
                return false;
            }
            if (!(/[a-z]/).test(data)) {
                setErrorState(getLabel('form_input_field_password_lowercase_error'));
                return false;
            }
            if (!(/[A-Z]/).test(data)) {
                setErrorState(getLabel('form_input_field_password_uppercase_error'));
                return false;
            }
            if (!(/[0-9]/).test(data)) {
                setErrorState(getLabel('form_input_field_password_numeric_error'));
                return false;
            }
            if (!(/[-_^$*.{}()?"!@#%&/[\],><':;|~`+=\\ ]/).test(data)) {
                setErrorState(getLabel('form_input_field_password_symbol_error'));
                return false;
            }
            if ((/^\s|\s$/).test(data)) {
                setErrorState(getLabel('form_input_field_password_space_error'));
                return false;
            }
        }

        const validationFromPattern = validationPattern(data);
        if (validationFromPattern) {
            setErrorState(validationFromPattern);
            return false;
        }

        setErrorState('');
        return true;
    }, [error, valueState, required, minLength, emailValidation, telValidation, passwordValidation, validationPattern, getLabel]);

    useEffect(() => {
        if (errorState) {
            validation();
        }
    }, [errorState, validation])

    useImperativeHandle(ref, () => {
        return {
            ...(inputRef.current || {}),
            validation: validation
        };
    }, [validation]);

    const onChangeHandle = useCallback((event) => {
        const value = event.target.value;
        setValueState(value);
        onChange(event);
    }, [onChange]);

    const onBlurHandle = useCallback((event) => {
        const value = event.target.value;
        validation(value);
        setValueState(value);
        onBlur(event);
    }, [validation, onBlur]);

    const togglePasswordVisibility = useCallback(() => {
        setShowPassword(prevShowPassword => !prevShowPassword);
    }, []);

    // Determine the input type - if it's a password field and showPassword is true, show as text
    const inputType = type === 'password' && showPassword ? 'text' : type;

    // Create additional props for password fields
    const additionalProps = {};
    if (type === 'password' && showPassword) {
        additionalProps['data-was-password'] = 'true';
    }

    return <div 
        className={`
            ${styles.fild} 
            ${className} 
            ${errorState ? styles.fild_error : ''} 
            ${styles['he_content_direction_' + heContentDirection]} 
            ${styles['he_placeholder_direction_' + hePlaceholderDirection]}
        `}
    >
        {label && <div className={styles.label}>{label}</div>}
        <div className={styles.error}>{errorState}</div>
        <div className={styles.input}>
            <KeyboardInput 
                {...props} 
                {...additionalProps}
                ref={inputRef} 
                onChange={onChangeHandle} 
                onBlur={onBlurHandle}
                type={inputType}
                value={valueState} 
                required={required}
                minLength={minLength}
            />
            {type === 'password' && valueState && (
                <div className={styles.passwordToggle} onClick={togglePasswordVisibility} aria-label={showPassword ? "Hide password" : "Show password"}>
                    {showPassword ? (
                        <svg viewBox="0 0 24 24" width="20" height="20">
                            <path fill="currentColor" d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
                        </svg>
                    ) : (
                        <svg viewBox="0 0 24 24" width="20" height="20">
                            <path fill="currentColor" d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                        </svg>
                    )}
                </div>
            )}
        </div>
    </div>;
});

export default FormInputField;